import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Button, Modal, Form, Input, Row, Col } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ShopOutlined,
  PhoneOutlined, MailOutlined, HomeOutlined, AuditOutlined
} from '@ant-design/icons';

const { TextArea } = Input;

const StoreMaster = () => {
  const [storeList, setStoreList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const apiUrl = 'http://your-api-endpoint.com/store';

  const fetchStores = async () => {
    try {
      const res = await fetch(apiUrl);
      const data = await res.json();
      setStoreList(data);
    } catch { /* silently fail */ }
  };

  useEffect(() => { fetchStores(); }, []);

  const openModal = (store = null) => {
    if (store) {
      setIsEditing(true);
      setEditingStoreId(store._id);
      form.setFieldsValue({ ...store });
    } else {
      setIsEditing(false);
      setEditingStoreId(null);
      form.resetFields();
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const url = isEditing ? `${apiUrl}/update/${editingStoreId}` : apiUrl;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error();
      Swal.fire('Success', `Store ${isEditing ? 'updated' : 'saved'}!`, 'success');
      setShowModal(false);
      fetchStores();
    } catch {
      Swal.fire('Error', 'Failed to save store details.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Delete Store?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete' });
    if (!result.isConfirmed) return;
    await fetch(`${apiUrl}/delete/${id}`, { method: 'DELETE' });
    Swal.fire('Deleted!', 'Store removed.', 'success');
    fetchStores();
  };

  const storeInfoFields = storeList.length > 0 ? [
    { icon: <ShopOutlined />, label: 'Store Name', value: storeList[0].storeName, color: '#2874f0', bg: '#e8f1ff' },
    { icon: <PhoneOutlined />, label: 'Phone', value: storeList[0].phoneNumber, color: '#059669', bg: '#ecfdf5' },
    { icon: <MailOutlined />, label: 'Email', value: storeList[0].email, color: '#d97706', bg: '#fffbeb' },
    { icon: <AuditOutlined />, label: 'GST Number', value: storeList[0].gstNo, color: '#7c3aed', bg: '#f5f3ff' },
    { icon: <HomeOutlined />, label: 'Address', value: storeList[0].address, color: '#e53935', bg: '#fef2f2' },
    { icon: <AuditOutlined />, label: 'FSSAI Number', value: storeList[0].fssaiNo, color: '#0891b2', bg: '#ecfeff' },
  ] : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Store Master</h2>
        </div>
        {storeList.length === 0 ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
            Add Store
          </Button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<EditOutlined />} onClick={() => openModal(storeList[0])} style={{ borderRadius: 4, borderColor: '#2874f0', color: '#2874f0' }}>Edit</Button>
            <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(storeList[0]._id)} style={{ borderRadius: 4 }}>Delete</Button>
          </div>
        )}
      </div>

      {storeList.length > 0 ? (
        <Row gutter={[16, 16]}>
          {storeInfoFields.map((item, i) => (
            <Col xs={24} sm={12} md={8} key={i}>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', padding: 20, display: 'flex', gap: 14, alignItems: 'flex-start', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', height: '100%' }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.color, fontSize: 20, flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>{item.label}</div>
                  <div style={{ fontSize: 14, color: '#212121', fontWeight: 500, lineHeight: 1.5 }}>{item.value || '—'}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', padding: 60, textAlign: 'center' }}>
          <ShopOutlined style={{ fontSize: 48, color: '#d1d5db', display: 'block', marginBottom: 12 }} />
          <div style={{ color: '#9ca3af', fontSize: 14 }}>No store details added yet.</div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} style={{ marginTop: 16, background: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }}>
            Add Store
          </Button>
        </div>
      )}

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{isEditing ? 'Edit Store' : 'Add Store'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="storeName" label="Store Name" rules={[{ required: true }]}>
              <Input prefix={<ShopOutlined />} size="large" placeholder="Store name" />
            </Form.Item>
            <Form.Item name="phoneNumber" label="Phone Number" rules={[{ required: true }]}>
              <Input prefix={<PhoneOutlined />} size="large" placeholder="Phone number" />
            </Form.Item>
            <Form.Item name="gstNo" label="GST Number" rules={[{ required: true }]}>
              <Input prefix={<AuditOutlined />} size="large" placeholder="GST number" />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
              <Input prefix={<MailOutlined />} size="large" placeholder="Email address" />
            </Form.Item>
            <Form.Item name="fssaiNo" label="FSSAI Number" rules={[{ required: true }]}>
              <Input prefix={<AuditOutlined />} size="large" placeholder="FSSAI number" />
            </Form.Item>
          </div>
          <Form.Item name="address" label="Address" rules={[{ required: true }]}>
            <TextArea rows={3} placeholder="Enter full store address" style={{ borderRadius: 4 }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting} style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {isEditing ? 'Update Store' : 'Save Store'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default StoreMaster;
